/** Buying a subscription, when there is no payment gateway.
 *
 * The company is not registered yet, so money moves by UPI or bank
 * transfer and a person confirms it. That makes this screen unusual: it is
 * not a checkout, it is a set of INSTRUCTIONS, and the thing that decides
 * whether it works is whether the learner quotes the reference. Without it
 * a bank credit is an amount, a date and a name that is often not the name
 * on the account — unmatchable.
 *
 * So the reference is the loudest thing on the payment step, it explains
 * WHY it matters rather than just asking, and it can be copied rather than
 * retyped. The three steps stay on one screen because a learner switching
 * to their banking app and back should not lose their place.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/billing";
import type { ManualOrder, PublicPlans } from "../../api/billing";
import { ApiError } from "../../api/http";
import { Button, Card, Input, Label, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { rupees } from "../../utils/rupees";

const PERIODS = ["monthly", "quarterly", "half_yearly", "yearly"] as const;
type Period = (typeof PERIODS)[number];

const MONTHS: Record<Period, number> = {
  monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12,
};

const PLAN_CODE = "learner";


/** A value someone has to copy into a banking app. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-2 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-ink">{value}</span>
        <button
          type="button"
          onClick={() => {
            // Clipboard access can be refused; the value is on screen
            // either way, so a failure costs nothing.
            void navigator.clipboard?.writeText(value).then(
              () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              },
              () => undefined,
            );
          }}
          className="rounded-control border border-line px-2 py-1 text-xs font-semibold text-muted hover:border-brand hover:text-brand"
        >
          {copied ? t("buy.copied") : "⧉"}
        </button>
      </span>
    </div>
  );
}

export function SubscribePage() {
  const t = useT();
  const { push } = useToast();
  const [plans, setPlans] = useState<PublicPlans | null>(null);
  const [period, setPeriod] = useState<Period>("monthly");
  const [order, setOrder] = useState<ManualOrder | null>(null);
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    api.publicPlans().then(setPlans).catch(() => setPlans(null));
  }, []);

  const plan = plans?.plans.find((p) => p.code === PLAN_CODE) ?? null;
  const priceFor = (p: Period) =>
    plan ? plan.monthly_price_minor * MONTHS[p] : 0;

  const fail = (err: unknown) =>
    push("error", err instanceof ApiError ? err.message : t("common.error.generic"));

  const begin = async () => {
    setBusy(true);
    try {
      setOrder(await api.startManualOrder(PLAN_CODE, period));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!order || !utr.trim()) return;
    setBusy(true);
    try {
      await api.reportPayment(order.id, utr.trim());
      setReported(true);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!plans) {
    return (
      <div className="grid place-items-center py-10">
        <Spinner />
      </div>
    );
  }

  // ---- step 3: told us, now waiting on a human --------------------------
  if (reported && order) {
    return (
      <div className="mx-auto max-w-xl">
        <Card>
          <p aria-hidden className="text-3xl">✓</p>
          <h1 className="mt-2 text-xl font-extrabold text-ink">{t("buy.waiting")}</h1>
          <p className="mt-2 text-sm text-muted">{t("buy.waitingBody")}</p>
          <p className="mt-4 text-sm text-muted">
            {t("approve.reference")}:{" "}
            <span className="font-mono font-semibold text-ink">{order.reference}</span>
          </p>
        </Card>
      </div>
    );
  }

  // ---- step 2: where to send the money ---------------------------------
  if (order) {
    const pay = order.pay_to;
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <h1 className="text-2xl font-extrabold text-ink">{t("buy.howToPay")}</h1>

        {!pay.configured ? (
          <Card>
            <p role="alert" className="text-sm text-danger">
              {t("buy.notConfigured")}
            </p>
          </Card>
        ) : (
          <>
            <Card>
              <p className="text-sm text-ink">
                {t("buy.step1", { amount: rupees(order.amount_minor) })}
              </p>
              <div className="mt-3 space-y-3">
                {pay.upi_id && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-muted">
                      {t("buy.upi")}
                    </p>
                    <CopyRow label={t("buy.upi")} value={pay.upi_id} />
                  </div>
                )}
                {pay.bank && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-muted">
                      {t("buy.bank")}
                    </p>
                    <CopyRow label={t("buy.accountName")} value={pay.bank.holder} />
                    <CopyRow label={t("buy.accountNumber")} value={pay.bank.account} />
                    <CopyRow label={t("buy.ifsc")} value={pay.bank.ifsc} />
                  </div>
                )}
                {pay.paypal_url && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-muted">
                      {t("buy.paypal")}
                    </p>
                    <a
                      href={pay.paypal_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm font-semibold text-brand underline"
                    >
                      {pay.paypal_url}
                    </a>
                  </div>
                )}
              </div>
            </Card>

            {/* The reference is the whole reconciliation story, so it is the
                loudest thing here and it says WHY rather than just asking. */}
            <Card>
              <p className="text-sm font-bold text-ink">{t("buy.step2")}</p>
              <p
                data-testid="payment-reference"
                className="mt-2 rounded-control bg-brand-soft px-4 py-3 text-center font-mono text-2xl font-extrabold tracking-widest text-brand-dark"
              >
                {order.reference}
              </p>
              <p className="mt-2 text-sm text-muted">{t("buy.referenceWhy")}</p>
            </Card>

            <Card>
              <p className="text-sm text-ink">{t("buy.step3")}</p>
              <div className="mt-3">
                <Label htmlFor="utr">{t("buy.utrLabel")}</Label>
                <Input
                  id="utr"
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  placeholder={t("buy.utrPlaceholder")}
                />
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={confirm} loading={busy} disabled={!utr.trim()}>
                  {t("buy.submitUtr")}
                </Button>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ---- step 1: how long ------------------------------------------------
  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-2xl font-extrabold text-ink">{t("buy.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("buy.subtitle")}</p>
      </div>

      <Card title={t("buy.pickPeriod")}>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              aria-pressed={period === p}
              className={`rounded-control border p-3 text-left ${
                period === p
                  ? "border-brand bg-brand-soft"
                  : "border-line hover:border-brand"
              }`}
            >
              <span className="block text-sm font-bold text-ink">
                {t(`buy.period.${p}` as never)}
              </span>
              <span className="block text-lg font-extrabold text-ink">
                {rupees(priceFor(p))}
              </span>
              {/* There is no discount for length, so showing the monthly
                  equivalent is honest rather than persuasive — it stops a
                  buyer hunting for a saving that is not there. */}
              <span className="block text-xs text-muted">
                {t("buy.perMonth", {
                  amount: rupees(priceFor(p) / MONTHS[p]),
                })}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={begin} loading={busy} disabled={!plan}>
            {t("buy.continue")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
